import { Send } from "lucide-react";
import { useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useListContext,
  useNotify,
  useRefresh,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { Contact, OutreachList, OutreachListKind, OutreachListMember } from "../types";

/**
 * Bulk-set Offer or Channel on the selected contacts — same "pick from a
 * real outreach_lists list, apply to every selected id" shape as
 * ContactOutreachAxisSelector's single-contact version, looped. Each
 * contact's existing active membership of this SAME kind (if any) is
 * soft-removed first, then the new one is inserted — mirrors
 * useSetOutreachMembership.ts, called per-contact here since hooks can't run
 * in a loop. A contact with no existing membership of this kind just gets
 * the insert (no remove step).
 */
export function BulkOutreachAxisButton({
  kind,
  label,
}: {
  kind: OutreachListKind;
  label: string;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const { onUnselectItems, selectedIds = [] } = useListContext<Contact>();
  const [open, setOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const { data: lists, isPending: isPendingLists } = useGetList<OutreachList>(
    kind === "offer" ? "offer" : "channels",
    { pagination: { page: 1, perPage: 100 }, sort: { field: "name", order: "ASC" } },
    { enabled: open },
  );

  if (!selectedIds.length) return null;

  const applyList = async (newListId: string | number) => {
    setIsApplying(true);
    try {
      const { data: existing } = await dataProvider.getList<OutreachListMember>(
        "outreach_list_members",
        {
          filter: {
            "contact_id@in": `(${selectedIds.join(",")})`,
            list_kind: kind,
            "removed_at@is": null,
          },
          pagination: { page: 1, perPage: selectedIds.length },
          sort: { field: "id", order: "ASC" },
        },
      );
      const existingByContact = new Map(
        existing.map((m) => [String(m.contact_id), m]),
      );

      await Promise.all(
        selectedIds.map(async (contactId) => {
          const current = existingByContact.get(String(contactId));
          if (current) {
            await dataProvider.update("outreach_list_members", {
              id: current.id,
              data: {
                removed_at: new Date().toISOString(),
                removed_by: identity?.fullName ?? "manual",
              },
              previousData: current,
            });
          }
          await dataProvider.create("outreach_list_members", {
            data: {
              list_id: newListId,
              contact_id: contactId,
              added_by: identity?.fullName ?? "manual",
              list_kind: kind,
            },
          });
        }),
      );

      notify("crm.common.bulk_update_success", {
        type: "success",
        messageArgs: { _: `${label} updated for ${selectedIds.length} contacts` },
      });
      setOpen(false);
      onUnselectItems();
      refresh();
    } catch (error) {
      notify("crm.common.bulk_update_error", {
        type: "error",
        messageArgs: { _: `Failed to update ${label.toLowerCase()}` },
      });
      console.error(`Bulk ${label} update failed:`, error);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9"
        onClick={() => setOpen(true)}
      >
        <Send />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && setOpen(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Set {label}</DialogTitle>
            <DialogDescription>
              Applies to all {selectedIds.length} selected contacts.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col space-y-2 items-start">
            {isPendingLists ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : lists && lists.length > 0 ? (
              lists.map((list) => (
                <Button
                  key={list.id}
                  type="button"
                  variant="ghost"
                  disabled={isApplying}
                  className="px-0 py-0 hover:bg-default dark:hover:bg-default mb-0"
                  onClick={() => applyList(list.id)}
                >
                  {list.name}
                </Button>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                No {label.toLowerCase()} lists yet.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
